import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Logo } from "@/components/logo"
import { MONRA_ESIM_DOWNLOAD_URL } from "@/lib/app-links"
import { getVisitorDevice, isMobileStoreDevice } from "@/lib/visitor-device"

type NavLink = {
  href: string
  label: string
} | {
  href: string
  label: string
  dropdown: {
    href: string
    label: string
    description: string
    variant: string
  }[]
}

interface NavbarProps {
  links: NavLink[]
  logoBehavior?: "scroll" | "navigate"
  logoVariant: "main" | "esim" | "remittance"
  isTransparent?: boolean
}

const variantStyles = {
  main: {
    accent: "text-primary",
    buttonClass: "bg-esim hover:bg-esim-hover text-esim-foreground",
  },
  esim: {
    accent: "text-esim-deep",
    buttonClass: "bg-esim hover:bg-esim-hover text-esim-foreground",
  },
  remittance: {
    accent: "text-remittance-deep",
    buttonClass: "bg-remittance hover:bg-remittance-hover text-remittance-foreground",
  },
}

export function Navbar({
  links,
  logoBehavior = "scroll",
  logoVariant,
  isTransparent = true,
}: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [activeHash, setActiveHash] = useState("")
  const [showDownloadApp, setShowDownloadApp] = useState(false)
  const [isScrolled, setIsScrolled] = useState(() => {
    if (typeof window === "undefined") return false
    return window.scrollY > 20
  })
  const location = useLocation()
  const styles = variantStyles[logoVariant]
  const accent = styles.accent
  const btnClass = styles.buttonClass
  const logoLink = "/"

  useEffect(() => {
    setShowDownloadApp(isMobileStoreDevice(getVisitorDevice()))
  }, [])

  // Handle scroll effect
  useEffect(() => {
    let rafId: number | null = null
    let settleTimer: number | null = null

    const syncScrollState = () => {
      setIsScrolled(window.scrollY > 20)
    }

    const handleScroll = () => {
      if (rafId !== null) return
      rafId = window.requestAnimationFrame(() => {
        syncScrollState()
        rafId = null
      })
    }

    syncScrollState()
    window.addEventListener("scroll", handleScroll, { passive: true })
    window.addEventListener("resize", handleScroll, { passive: true })
    window.addEventListener("pageshow", handleScroll)

    // Some mobile browsers restore scroll after mount without dispatching scroll immediately.
    settleTimer = window.setTimeout(syncScrollState, 350)

    return () => {
      window.removeEventListener("scroll", handleScroll)
      window.removeEventListener("resize", handleScroll)
      window.removeEventListener("pageshow", handleScroll)
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer)
      }
    }
  }, [])

  useEffect(() => {
    const sectionIds = links
      .filter((link) => !("dropdown" in link) && link.href.startsWith("#"))
      .map((link) => link.href.slice(1))

    if (sectionIds.length === 0) return

    let rafId: number | null = null

    const syncActiveSection = () => {
      const marker = window.scrollY + 96
      let currentHash = ""

      sectionIds.forEach((id) => {
        const section = document.getElementById(id)
        if (section && section.offsetTop <= marker) {
          currentHash = `#${id}`
        }
      })

      setActiveHash(currentHash)
    }

    const handlePositionChange = () => {
      if (rafId !== null) return
      rafId = window.requestAnimationFrame(() => {
        syncActiveSection()
        rafId = null
      })
    }

    syncActiveSection()
    window.addEventListener("scroll", handlePositionChange, { passive: true })
    window.addEventListener("resize", handlePositionChange, { passive: true })
    window.addEventListener("hashchange", handlePositionChange)

    return () => {
      window.removeEventListener("scroll", handlePositionChange)
      window.removeEventListener("resize", handlePositionChange)
      window.removeEventListener("hashchange", handlePositionChange)
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId)
      }
    }
  }, [links])

  useEffect(() => {
    if (!isOpen) return

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isOpen])

  const isLinkActive = (href: string) => {
    if (href.startsWith("#")) return activeHash === href
    return location.pathname === href
  }

  const handleLogoClick = (e: React.MouseEvent) => {
    if (logoBehavior === "navigate") {
      return
    }
    e.preventDefault()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <header className={cn(
      "top-0 z-50 w-full isolate pointer-events-auto transition-[background,box-shadow] duration-200",
        isTransparent ? "fixed" : "sticky",
        isOpen
          ? "bg-background shadow-[0_1px_0_rgba(0,0,0,0.08)]"
          : isScrolled || !isTransparent
          ? "bg-background/95 shadow-[0_1px_0_rgba(0,0,0,0.08)] backdrop-blur-md"
          : "bg-background/80"
    )}>
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link
          to={logoLink}
          className="min-w-0 transition-opacity hover:opacity-80"
          onClick={handleLogoClick}
        >
          <Logo variant={logoVariant} size="md" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-1 lg:gap-4 md:flex">
          {links.map((link) => (
            "dropdown" in link && link.dropdown ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <button
                  className={cn(
                    "flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold transition-colors",
                    isTransparent && !isScrolled
                      ? "text-foreground/80 hover:bg-muted hover:text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    dropdownOpen && "text-foreground"
                  )}
                >
                  {link.label}
                  <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", dropdownOpen && "rotate-180")} />
                </button>

                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute left-0 top-full pt-2 w-72 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="rounded-2xl border border-border bg-card p-1.5 shadow-[0_6px_16px_rgba(0,0,0,0.12)]">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="flex items-start gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-muted"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <Logo variant={item.variant as any} size="sm" showText={false} className="mt-0.5" />
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-semibold text-foreground">{item.label}</span>
                            <span className="text-xs text-muted-foreground">{item.description}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a
                key={link.href}
                href={link.href}
                aria-current={isLinkActive(link.href) ? "location" : undefined}
                className={cn(
                  "rounded-full px-4 py-2 text-sm font-semibold transition-colors",
                  isLinkActive(link.href)
                    ? isTransparent && !isScrolled ? "text-foreground" : accent
                    : isTransparent && !isScrolled
                      ? "text-foreground/80 hover:bg-muted hover:text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                {link.label}
              </a>
            )
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden items-center gap-4 md:flex">
          {logoVariant === "main" && (
            <Button asChild className={cn("px-6", btnClass)}>
              <a href="/esim">Get Monra eSIM</a>
            </Button>
          )}
          {logoVariant === "esim" && showDownloadApp && (
            <Button asChild className={cn("px-6", btnClass)}>
              <a href={MONRA_ESIM_DOWNLOAD_URL} target="_blank" rel="noreferrer">Download app</a>
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          type="button"
          className={cn(
            "relative z-[60] ml-3 flex h-10 w-10 shrink-0 touch-manipulation items-center justify-center rounded-xl transition-colors md:hidden",
            "hover:bg-muted"
          )}
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
        </nav>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="absolute inset-x-0 top-full z-40 h-[calc(100dvh-4rem)] bg-background animate-in slide-in-from-top-2 md:hidden">
            <div className="flex h-full flex-col overflow-y-auto px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-6 sm:px-6 sm:py-8">
              <div className="space-y-2">
                {links.map((link) => (
                  "dropdown" in link && link.dropdown ? (
                    <div key={link.label} className="space-y-4">
                      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{link.label}</div>
                      <div className="grid gap-6 pl-2">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.href}
                            to={item.href}
                            className="flex items-start gap-4"
                            onClick={() => setIsOpen(false)}
                          >
                            <Logo variant={item.variant as any} size="sm" showText={false} className="mt-1" />
                            <div className="flex flex-col gap-1">
                              <span className="text-lg font-semibold text-foreground">{item.label}</span>
                              <span className="text-sm text-muted-foreground">{item.description}</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <a
                      key={link.href}
                      href={link.href}
                      aria-current={isLinkActive(link.href) ? "location" : undefined}
                      className={cn(
                        "block px-3 py-3 text-base font-medium transition-colors sm:text-lg",
                        isLinkActive(link.href) ? accent : "text-foreground hover:text-muted-foreground"
                      )}
                      onClick={() => {
                        setActiveHash(link.href)
                        setIsOpen(false)
                      }}
                    >
                      {link.label}
                    </a>
                  )
                ))}
              </div>

              {logoVariant !== "remittance" && (logoVariant === "main" || showDownloadApp) && (
                <div className="mt-auto pt-8">
                  <Button asChild className={cn("w-full py-7 text-lg", btnClass)}>
                    {logoVariant === "main" ? (
                      <a href="/esim" onClick={() => setIsOpen(false)}>Get Monra eSIM</a>
                    ) : (
                      <a href={MONRA_ESIM_DOWNLOAD_URL} target="_blank" rel="noreferrer" onClick={() => setIsOpen(false)}>Download app</a>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
      )}
    </header>
  )
}
